import { useId, type InputHTMLAttributes, type ReactNode, type Ref } from 'react';
import { Icon } from './Icon';
import { VisuallyHidden } from './VisuallyHidden';
import styles from './Checkbox.module.css';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Visible label next to the box. Without it, pass `aria-label`. */
  label?: ReactNode;
  /** Secondary text below the label, announced as the accessible description. */
  description?: ReactNode;
  /** Label and description only for assistive technology, e.g. in a table with column headers. */
  labelHidden?: boolean;
  invalid?: boolean;
  ref?: Ref<HTMLInputElement>;
}

/** Native checkbox with a custom look; the whole row is clickable. */
export function Checkbox({
  label,
  description,
  labelHidden = false,
  invalid = false,
  id,
  className,
  ...rest
}: CheckboxProps) {
  const generated = useId();
  const inputId = id ?? generated;
  const labelId = label ? `${inputId}-label` : undefined;
  const descriptionId = description ? `${inputId}-description` : undefined;
  const box = (
    <span className={styles.box}>
      <input
        id={inputId}
        type="checkbox"
        className={[styles.input, invalid && styles.invalid].filter(Boolean).join(' ')}
        aria-invalid={invalid || undefined}
        // The wrapping label would include the description in the name.
        aria-labelledby={labelId}
        aria-describedby={descriptionId}
        {...rest}
      />
      <Icon name="check" size="xs" className={styles.mark} />
    </span>
  );
  if (!label) return box;
  if (labelHidden) {
    return (
      <label className={[styles.row, className].filter(Boolean).join(' ')}>
        {box}
        <VisuallyHidden id={labelId}>{label}</VisuallyHidden>
        {description && <VisuallyHidden id={descriptionId}>{description}</VisuallyHidden>}
      </label>
    );
  }
  return (
    <label className={[styles.row, className].filter(Boolean).join(' ')}>
      {box}
      <span className={styles.text}>
        <span id={labelId} className={styles.label}>
          {label}
        </span>
        {description && (
          <span id={descriptionId} className={styles.description}>
            {description}
          </span>
        )}
      </span>
    </label>
  );
}
